import analytics from "./analytics/index.mjs";
import { delay } from "./asyncUtils.mjs";
function defaultExponentialBackoff(attempts) {
  const base = 100;
  const backoff = Math.pow(2, attempts - 1);
  return base * backoff;
}
async function defaultShouldRetryError() {
  return true;
}
async function defaultShouldRetryResponse(response) {
  return [
    408,
    // Request Timeout
    429,
    // Too Many Requests
    500,
    // Internal Server Error
    502,
    // Bad Gateway
    503,
    // Service Unavailable
    504
    // Gateway Timeout
  ].includes(response.status);
}
async function trackedRequest(fetcher, request, options) {
  var _a;
  const { headers, body, tracking } = options;
  const method = ((_a = options.method) == null ? void 0 : _a.toUpperCase()) || "GET";
  const url = new URL(request);
  const shouldRetryError = options.retryError || defaultShouldRetryError;
  const shouldRetryResponse = options.retryResponse || defaultShouldRetryResponse;
  const retryDelay = options.retryDelay || defaultExponentialBackoff;
  const maxAttempts = Math.max(
    Number.isSafeInteger(options.maxAttempts) ? options.maxAttempts : 0,
    1
  );
  const startTime = performance.now();
  let error, response, retry;
  let attempts = 0;
  do {
    attempts += 1;
    error = response = void 0;
    try {
      response = await fetcher(url.href, { method, headers, body });
      retry = await shouldRetryResponse(response);
    } catch (err) {
      error = err;
      retry = await shouldRetryError(error);
    }
    if (!retry)
      break;
    if (attempts < maxAttempts)
      await delay(retryDelay(attempts));
  } while (attempts < maxAttempts);
  const endTime = performance.now();
  const success = !retry && response && response.ok;
  let failedResponseText;
  if (!success && response) {
    failedResponseText = await response.text();
    response.text = async () => failedResponseText;
  }
  analytics.pushEvent("PlatformRequest", {
    platform: tracking.platform,
    requestTries: attempts,
    requestMs: endTime - startTime,
    source: tracking.source,
    method,
    href: url.href,
    origin: url.origin,
    pathname: url.pathname,
    search: url.search,
    statusCode: response == null ? void 0 : response.status,
    statusText: response == null ? void 0 : response.statusText,
    errorName: error == null ? void 0 : error.name,
    errorMessage: error == null ? void 0 : error.message,
    failedResponseText,
    result: success ? "Success" : "Failure"
  });
  if (error)
    throw error;
  return response;
}
async function trackedJsonRequest(fetcher, request, options) {
  return trackedRequest(fetcher, request, {
    ...options,
    headers: {
      ...options.headers || {},
      Accept: "application/json",
      "Content-Type": "application/json"
    },
    body: options.body ? JSON.stringify(options.body) : void 0
  });
}
export {
  defaultShouldRetryResponse,
  trackedJsonRequest,
  trackedRequest
};
